import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ABOUT_BG, SITE } from "../../lib/config";
import { IbexMark } from "./IbexMark";

const values = [
  {
    letter: "I",
    label: "Invest",
    body: "We invest strategically — in homes, in land, and in the people we do business with.",
  },
  {
    letter: "B",
    label: "Build",
    body: "We build valuable projects — and a reputation we protect on every deal.",
  },
  {
    letter: "E",
    label: "Elevate",
    body: "We elevate properties, neighborhoods, and communities.",
  },
  {
    letter: "X",
    label: "Expand",
    body: "We expand into new markets without ever outgrowing our word.",
  },
];

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const About = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const midY = useTransform(scrollYProgress, [0, 1], ["4%", "-4%"]);

  return (
    <section
      id="about"
      ref={ref}
      className="relative overflow-hidden section-pad"
      style={{ background: "#122B20", color: "#FAF7F2" }}
      data-testid="about-section"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 -z-0"
        aria-hidden="true"
      >
        <img
          src={ABOUT_BG}
          alt=""
          className="absolute inset-0 w-full h-[120%] object-cover"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(18,43,32,0.85) 0%, rgba(18,43,32,0.92) 100%)",
          }}
        />
      </motion.div>

      {/* Ibex watermark */}
      <motion.div
        style={{ y: midY }}
        className="pointer-events-none absolute right-[-4%] top-1/2 -translate-y-1/2 w-[560px] h-[560px] md:w-[720px] md:h-[720px]"
        aria-hidden="true"
      >
        <IbexMark
          className="w-full h-full"
          strokeWidth={1}
        />
        <style>{`
          [data-testid="about-section"] svg { color: rgba(198,161,91,0.10); }
        `}</style>
      </motion.div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <p className="eyebrow">About Ibex</p>
          <h2
            className="font-heading font-medium mt-4 max-w-3xl"
            style={{
              fontSize: "clamp(2rem, 4.2vw, 3.6rem)",
              lineHeight: 1.1,
              color: "#FAF7F2",
              letterSpacing: "-0.015em",
            }}
          >
            We See What Your Property <span className="gold-italic">Can Become.</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14 items-start"
        >
          <div className="lg:col-span-2 space-y-6 max-w-2xl">
            <p
              className="font-body"
              style={{ color: "rgba(250,247,242,0.9)", lineHeight: 1.75, fontSize: "1.02rem" }}
            >
              Ibex Acquisitions &amp; Development is a family-owned company
              founded by <span style={{ color: "#C6A15B" }}>Brandon</span> and{" "}
              <span style={{ color: "#C6A15B" }}>Katana</span> — a husband-and-wife
              team who believe real estate should be done with straight talk,
              fair numbers, and follow-through.
            </p>
            <p
              className="font-body"
              style={{ color: "rgba(250,247,242,0.85)", lineHeight: 1.75, fontSize: "1.02rem" }}
            >
              We built this company for the deals other buyers walk away from.
              Back taxes, title tangles, inherited paperwork, complicated
              stories — that’s terrain we know how to navigate. Where others
              see problems, we see potential.
            </p>
            <p
              className="font-body"
              style={{ color: "rgba(250,247,242,0.85)", lineHeight: 1.75, fontSize: "1.02rem" }}
            >
              When we buy your property, it isn’t disappearing into a
              spreadsheet. We invest in houses and land, build and restore, and
              develop projects that elevate the communities around them.
              Selling to Ibex means your property’s next chapter is in the
              hands of people who actually care what gets built.
            </p>
          </div>

          {/* Founder placeholder card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.25, ease: "easeOut" }}
            className="glass-dark p-5 md:p-6 mx-auto max-w-sm w-full"
            data-testid="about-founders-card"
          >
            <div
              className="w-full aspect-[4/5] rounded-xl relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(160deg, rgba(198,161,91,0.20) 0%, rgba(27,67,50,0.55) 100%)",
                border: "1px solid rgba(198,161,91,0.5)",
              }}
            >
              <img
                src={SITE.founderPhoto}
                alt="Brandon and Katana — founders of Ibex Acquisitions & Development"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                data-testid="about-founders-photo"
              />
              <div
                className="absolute bottom-0 inset-x-0 h-24"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(18,43,32,0) 0%, rgba(18,43,32,0.7) 100%)",
                }}
              />
            </div>
            <p
              className="mt-5 font-heading text-center"
              style={{ color: "#FAF7F2", fontSize: "1.05rem" }}
            >
              Brandon &amp; Katana
            </p>
            <p
              className="text-center mt-1"
              style={{
                color: "#C6A15B",
                fontSize: "0.7rem",
                letterSpacing: "0.24em",
                textTransform: "uppercase",
              }}
            >
              Founders
            </p>
          </motion.div>
        </motion.div>

        {/* I.B.E.X. values */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
          }}
          className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {values.map((v) => (
            <motion.div
              key={v.letter}
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              data-testid={`value-${v.letter}`}
            >
              <div className="flex items-baseline gap-4">
                <div
                  className="font-heading font-medium leading-none"
                  style={{
                    color: "#C6A15B",
                    fontSize: "3rem",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {v.letter}
                </div>
                <div
                  className="font-heading font-medium"
                  style={{ color: "#FAF7F2", fontSize: "1.5rem" }}
                >
                  {v.label}
                </div>
              </div>
              <div
                className="mt-4 h-px"
                style={{ background: "rgba(198,161,91,0.3)" }}
              />
              <p
                className="mt-4 font-body"
                style={{
                  color: "rgba(250,247,242,0.82)",
                  lineHeight: 1.7,
                  fontSize: "0.95rem",
                }}
              >
                {v.body}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-center gap-5"
        >
          <p
            className="font-heading text-xl md:text-2xl"
            style={{ color: "#FAF7F2" }}
          >
            Builders and development partners:{" "}
            <span className="gold-italic">we’d love to talk.</span>
          </p>
          <button
            onClick={() => navigate("/builders")}
            className="btn-ghost on-dark"
            data-testid="about-partner-cta"
          >
            Partner With Us
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
